/**
 * Which polls this browser has already put in front of this participant.
 *
 * The prompt has to be remembered, not merely closed, and for two reasons that
 * look different and are the same bug.
 *
 * **Dismissing did not stick.** `onClose` only sets `pollVotingOpen` to false.
 * A poll that is still active and still unanswered immediately qualifies to be
 * prompted again, so the dialog reopened the instant it was closed.
 *
 * **Voting did not stick either.** `handleVotePoll` awaits the network BEFORE
 * recording the vote locally, while the dialog calls `onClose` straight after
 * submitting. For the length of that round trip the poll is still unanswered as
 * far as this client knows, so the same dialog reappeared over a vote that was
 * already on its way.
 *
 * Both vanish if a poll is marked when it is SHOWN rather than when it is
 * answered. Shown once is shown; vote, cancel, X and a slow connection all then
 * behave identically, and nothing has to race.
 *
 * Persisted rather than held in memory, because a reload is not a decision to
 * un-dismiss something. It does not take the vote away — the poll is still in
 * the Polls panel and can still be answered there. It only stops the room
 * interrupting someone who has already been asked.
 *
 * Scoped per meeting: the same browser in a different meeting starts clean.
 */

const KEY = 'inmeet_poll_prompted'

/**
 * How many meetings to remember.
 *
 * A bound rather than a sweep by age: this is a browser-local convenience with
 * no clock to trust, and twenty meetings of poll ids is a few hundred bytes.
 * Without it the entry would grow for the life of the browser profile.
 */
const MAX_MEETINGS = 20

type Store = Record<string, string[]>

/**
 * The two methods this needs.
 *
 * Injectable so the rules can be exercised without a DOM — the test runner is
 * deliberately `node`, and pulling in a browser environment to check a
 * dismissal would cost every other suite seconds per run. Resolving `window`
 * lazily also means a module-level import cannot throw where there is no
 * window at all.
 */
export type PromptStorage = {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

function defaultStorage(): PromptStorage | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null
  } catch {
    // Reading `localStorage` itself throws when cookies are blocked.
    return null
  }
}

/**
 * Storage is best-effort throughout.
 *
 * It throws in private browsing, when a quota is hit, and when the page is
 * sandboxed. Losing the memory of a prompt means someone is asked twice; taking
 * the meeting down over it would be far worse, so every path here fails soft.
 */
function read(storage: PromptStorage | null): Store {
  if (!storage) return {}
  try {
    const raw = storage.getItem(KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    // Each value is validated rather than trusted: this key is editable by
    // anyone with devtools, and a non-array would break every caller.
    const out: Store = {}
    for (const [meeting, ids] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(ids)) out[meeting] = ids.filter((id): id is string => typeof id === 'string')
    }
    return out
  } catch {
    return {}
  }
}

function write(storage: PromptStorage | null, store: Store): void {
  if (!storage) return
  try {
    storage.setItem(KEY, JSON.stringify(store))
  } catch {
    // Quota, private mode, sandbox. The prompt simply is not remembered.
  }
}

/** Polls already shown to this participant in this meeting. */
export function promptedPollIds(
  meetingId: string | null | undefined,
  storage: PromptStorage | null = defaultStorage(),
): string[] {
  if (!meetingId) return []
  return read(storage)[meetingId] ?? []
}

/**
 * Record that a poll has been shown.
 *
 * Returns the new list so a caller holding it in state can update without a
 * second read.
 */
export function rememberPrompted(
  meetingId: string | null | undefined,
  pollId: string,
  storage: PromptStorage | null = defaultStorage(),
): string[] {
  if (!meetingId || !pollId) return promptedPollIds(meetingId, storage)

  const store = read(storage)
  const current = store[meetingId] ?? []
  if (current.includes(pollId)) return current

  const next = [...current, pollId]
  store[meetingId] = next

  // Oldest meetings drop out first. `Object.keys` preserves insertion order for
  // string keys, and a meeting touched again is not re-inserted — so this is
  // "first written" rather than "least recently used", which is close enough
  // for a cap that exists only to stop unbounded growth.
  const meetings = Object.keys(store)
  if (meetings.length > MAX_MEETINGS) {
    for (const stale of meetings.slice(0, meetings.length - MAX_MEETINGS)) delete store[stale]
  }

  write(storage, store)
  return next
}
